import React, { useState, useEffect } from 'react';

const ServicesCTAModal = ({ isOpen, onClose, initialData }) => {
  const [ctaData, setCtaData] = useState({
    title: "",
    subtitle: "",
    backgroundColor: "",
    primaryButton: {
      text: "",
      link: "",
      bgColor: "",
      textColor: ""
    },
    secondaryButton: {
      text: "",
      link: "",
      bgColor: "",
      textColor: "",
      borderColor: ""
    }
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchCurrentData = async () => {
      if (isOpen && !initialData) {
        try {
          setIsLoading(true);
          const response = await fetch(`${import.meta.env.VITE_API_URL}/services/cta/`);
          if (response.ok) {
            const data = await response.json();
            setCtaData(data);
          }
        } catch (error) {
          console.error('Error fetching services CTA data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCurrentData();
  }, [isOpen, initialData]);

  useEffect(() => {
    if (initialData) {
      setCtaData(initialData);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCtaData(prev => ({ ...prev, [name]: value }));
  };

  const handleButtonChange = (buttonType, field, value) => {
    setCtaData(prev => ({
      ...prev,
      [buttonType]: { ...prev[buttonType], [field]: value }
    }));
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/services/cta/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ctaData)
      });

      if (response.ok) {
        const updatedData = await response.json();
        onClose();
        window.location.reload();
      } else {
        console.error('Failed to update services CTA data');
        alert('Failed to save changes. Please try again.');
      }
    } catch (error) {
      console.error('Error updating services CTA data:', error);
      alert('An error occurred while saving. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <span className="loading loading-spinner loading-lg"></span>
          <p>Loading services CTA data...</p>
        </div>
      </dialog>
    );
  }

  return (
    <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
      <div className="modal-box w-11/12 max-w-3xl max-h-[90vh] relative">
        <button onClick={onClose} className="btn btn-sm btn-circle absolute right-2 top-2">✕</button>
        <h3 className="font-bold text-lg mb-6">Edit Services CTA Section</h3>
        
        <div className="overflow-y-auto max-h-[70vh] pr-2 space-y-4">
          <div className="form-control">
            <label className="label">
              <span className="label-text">Background Color</span>
            </label>
            <input 
              type="color" 
              name="backgroundColor"
              value={ctaData.backgroundColor || "#0760ad"} 
              onChange={handleChange} 
              className="input input-bordered w-20 h-10 p-1" 
            />
          </div>
          
          <div className="form-control">
            <label className="label">
              <span className="label-text">Title</span>
            </label>
            <input 
              type="text" 
              name="title"
              value={ctaData.title || ""} 
              onChange={handleChange} 
              className="input input-bordered w-full" 
            />
          </div>
          
          <div className="form-control">
            <label className="label">
              <span className="label-text">Subtitle</span>
            </label>
            <input 
              type="text" 
              name="subtitle"
              value={ctaData.subtitle || ""} 
              onChange={handleChange} 
              className="input input-bordered w-full" 
            />
          </div>
          
          <div className="card bg-base-100 shadow-sm p-4 mt-6">
            <h4 className="font-bold mb-4">Primary Button</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Button Text</span>
                </label>
                <input 
                  type="text" 
                  value={ctaData.primaryButton?.text || ""} 
                  onChange={(e) => handleButtonChange("primaryButton", "text", e.target.value)} 
                  className="input input-bordered w-full" 
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Button Link</span>
                </label>
                <input 
                  type="text" 
                  value={ctaData.primaryButton?.link || ""} 
                  onChange={(e) => handleButtonChange("primaryButton", "link", e.target.value)} 
                  className="input input-bordered w-full" 
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Background Color</span>
                </label>
                <input 
                  type="color" 
                  value={ctaData.primaryButton?.bgColor || "#FFFFFF"} 
                  onChange={(e) => handleButtonChange("primaryButton", "bgColor", e.target.value)} 
                  className="input input-bordered w-20 h-10 p-1" 
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Text Color</span>
                </label>
                <input 
                  type="color" 
                  value={ctaData.primaryButton?.textColor || "#0760ad"} 
                  onChange={(e) => handleButtonChange("primaryButton", "textColor", e.target.value)} 
                  className="input input-bordered w-20 h-10 p-1" 
                />
              </div>
            </div>
          </div>
          
          <div className="card bg-base-100 shadow-sm p-4 mt-4">
            <h4 className="font-bold mb-4">Secondary Button</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Button Text</span>
                </label>
                <input 
                  type="text" 
                  value={ctaData.secondaryButton?.text || ""} 
                  onChange={(e) => handleButtonChange("secondaryButton", "text", e.target.value)} 
                  className="input input-bordered w-full" 
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Button Link</span>
                </label>
                <input 
                  type="text" 
                  value={ctaData.secondaryButton?.link || ""} 
                  onChange={(e) => handleButtonChange("secondaryButton", "link", e.target.value)} 
                  className="input input-bordered w-full" 
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Background Color</span>
                </label>
                <input 
                  type="color" 
                  value={ctaData.secondaryButton?.bgColor || "transparent"} 
                  onChange={(e) => handleButtonChange("secondaryButton", "bgColor", e.target.value)} 
                  className="input input-bordered w-20 h-10 p-1" 
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Text Color</span>
                </label>
                <input 
                  type="color" 
                  value={ctaData.secondaryButton?.textColor || "#FFFFFF"} 
                  onChange={(e) => handleButtonChange("secondaryButton", "textColor", e.target.value)} 
                  className="input input-bordered w-20 h-10 p-1" 
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Border Color</span>
                </label>
                <input 
                  type="color" 
                  value={ctaData.secondaryButton?.borderColor || "#FFFFFF"} 
                  onChange={(e) => handleButtonChange("secondaryButton", "borderColor", e.target.value)} 
                  className="input input-bordered w-20 h-10 p-1" 
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="modal-action mt-6">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            Save Changes
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default ServicesCTAModal;